import React, { useEffect, useState } from 'react';
import { FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LockIcon from '@mui/icons-material/Lock';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import BillModal from './BillModal';
import ThankYouModal from './ThankYouModal';
import type { FieldType, UploadOrderRequestType } from '../../types/orderTypes';
import orderThunk from '../../store/order/orderThunk';
import userThunk from '../../store/user/userThunk';
import { SkipTraceFileTypeENUM } from '../../utils/skipTraceFileHeaders';
import config from '../../config';
import { setFileMetadata, unsetUploadFile } from '../../store/order/orderSlice';

type PropType = {
  open: boolean;
  onClose: () => void;
  file?: File | null;
};

const Bill: React.FC<PropType> = (props) => {
  const userInfo = useAppSelector((state) => state.userSlice.user);
  const fileInfo = useAppSelector((state) => state.orderSlice.uploadFile);
  const isGetUploadFile = useAppSelector((state) => state.orderSlice.uploadFile.name);
  const fieldArray = useAppSelector((state) => state.orderSlice.uploadFile.fields);
  const [requiredEmail, setRequiredEmail] = useState(
    () => Boolean(fileInfo.metadata?.email),
  );
  const [requiredBlacklist, setRequiredBlacklist] = useState(
    () => Boolean(fileInfo.metadata?.blacklist),
  );
  const [requiredDnc, setRequiredDnc] = useState(
    () => Boolean(fileInfo.metadata?.dnc),
  );
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [isThankYouOpen, setIsThankYouOpen] = useState(false);
  const [estimatedMinutes, setEstimatedMinutes] = useState(30);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEmailFree = config.freeEmailSubscriptions.includes(userInfo.subscription?.type.toLowerCase() || '');

  /* eslint-disable no-var */
  // Use 3 decimals so values like 0.015 don't round to 0.01 via float + toFixed(2)
  var currentPricePerRecord = userInfo.rowCost.toFixed(3);
  var currentDiscount = useAppSelector((state) => state.orderSlice.discount);
  var totalPrice = 0;
  var emailPricePerRecord = 0;
  var blacklistPricePerRecord = 0;
  // /* eslint-enable no-var */
  const calculateTotalCost = (
    baseCost: number, records: number, discount: number, extraCharges: number,
  ) => {
    if (discount !== 0 &&
      userInfo.code === config.firstTimeCoupon.code && records > config.firstTimeCoupon.upto) {
      const discountAmount = config.firstTimeCoupon.upto * (baseCost + extraCharges) * discount;
      return (records * (baseCost + extraCharges)) - discountAmount;
    }
    return (baseCost + extraCharges) * records * (1 - discount);
  };

  if (
    (SkipTraceFileTypeENUM.BLACKLISTED === fileInfo.skipTraceType ||
      SkipTraceFileTypeENUM.DNC === fileInfo.skipTraceType) &&
    fileInfo.metadata?.blacklist_columns &&
    fileInfo.metadata?.blacklist_columns.length > 0
  ) {
    let baseCost = config.blacklistPricePerRecord;
    if (fileInfo.metadata?.sourceUserFileId && userInfo.isBlacklistFree) {
      baseCost = 0;
    }
    totalPrice = calculateTotalCost(
      baseCost,
      fileInfo.records - 1,
      currentDiscount,
      0,
    );
    currentPricePerRecord = baseCost.toFixed(3);
  } else {
    if (requiredEmail && !isEmailFree) {
      emailPricePerRecord = config.emailPricePerRecord;
    }
    if (requiredBlacklist && !userInfo.isBlacklistFree) {
      blacklistPricePerRecord = config.blacklistPricePerRecord;
    }
    const extraCharges = emailPricePerRecord + blacklistPricePerRecord;
    if (SkipTraceFileTypeENUM.BUSSINESS === fileInfo.skipTraceType) {
      currentDiscount = 0;
      totalPrice = (fileInfo.records - 1) * (userInfo.llcCost + extraCharges);
      currentPricePerRecord = userInfo.llcCost.toFixed(3);
    } else {
      const baseCost = userInfo.rowCost;
      totalPrice = calculateTotalCost(
        baseCost,
        fileInfo.records - 1,
        currentDiscount,
        extraCharges,
      );
      currentPricePerRecord = userInfo.rowCost.toFixed(3);
    }
  }
  const discount = (currentDiscount * 100).toFixed(2);

  useEffect(() => {
    if (!isGetUploadFile && !isThankYouOpen) {
      navigate('/skip-trace');
    }
  }, [isGetUploadFile, isThankYouOpen, navigate]);

  useEffect(() => {
    if (SkipTraceFileTypeENUM.BLACKLISTED !== fileInfo.skipTraceType &&
      SkipTraceFileTypeENUM.DNC !== fileInfo.skipTraceType) {
      dispatch(
        setFileMetadata(
          { email: requiredEmail, blacklist: requiredBlacklist, dnc: requiredDnc },
        ),
      );
    }
  }, [requiredEmail, requiredBlacklist, requiredDnc, dispatch, fileInfo.skipTraceType]);

  const onClickPayBulkSkip = async () => {
    try {
      setIsSubmiting(true);
      const newFieldArray = fieldArray.map((item) => {
        const newItem: FieldType = {
          oldField: item.oldField,
          newField: item.oldField,
        };
        return newItem;
      });
      const fileRequest: UploadOrderRequestType = {
        file: fileInfo.csvBase64,
        fileName: fileInfo.name,
        fields: newFieldArray,
        records: fileInfo.records - 1,
        metadata: fileInfo.metadata,
        type: fileInfo.skipTraceType,
      };
      const isScrubSkipTrace =
        fileInfo.skipTraceType === SkipTraceFileTypeENUM.BLACKLISTED ||
        fileInfo.skipTraceType === SkipTraceFileTypeENUM.DNC;
      if (props.file) {
        await dispatch(orderThunk.uploadOriginalFile({
          file: props.file, fileName: fileInfo.name,
        })).unwrap();
      }
      await dispatch(orderThunk.uploadFile(fileRequest)).unwrap();
      setEstimatedMinutes(isScrubSkipTrace ? 10 : 30);
      setIsThankYouOpen(true);
      await dispatch(userThunk.loginByToken()).unwrap();
      await dispatch(userThunk.getOrderCount()).unwrap();
    } catch (error) {
      toast.error(error);
    } finally {
      setIsSubmiting(false);
    }
  };

  const handleGoToDashboard = () => {
    dispatch(unsetUploadFile());
    navigate('/dashboard');
  };

  const handleEmailRadioChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setRequiredEmail(value === 'Yes');
  };

  const handleBlacklistRadioChange =
    (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
      if (value === 'No') {
        setRequiredDnc(false);
      }
      setRequiredBlacklist(value === 'Yes');
    };

  const handleDncRadioChange =
    (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
      setRequiredDnc(value === 'Yes');
    };

  const sectionArray = [
    {
      title: 'Skip Trace Type:',
      info: <h2>{fileInfo.skipTraceType}</h2>,
    },
    {
      title: 'Total Records:',
      info: <h2>{fileInfo.records - 1}</h2>,
    },
    {
      title: 'Discount:',
      info: <h2>{discount}%</h2>,
    },
    {
      title: 'Price/record:',
      info: <h2>${currentPricePerRecord}</h2>,
    },
    {
      title: 'Email Price/record:',
      info: <h2>${config.emailPricePerRecord}</h2>,
    },
    {
      title: 'Blacklist Price/record:',
      info: <h2>${config.blacklistPricePerRecord}</h2>,
    },
    {
      title: 'Would you like to include emails?',
      info: (
        <RadioGroup
          row
          aria-labelledby="demo-form-control-label-placement"
          name="position"
          value={requiredEmail ? 'Yes' : 'No'}
          onChange={handleEmailRadioChange}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio size="small" />}
            label="Yes"
          />
          <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
        </RadioGroup>
      ),
    },
    {
      title: 'Would you like to exclude litigator phone numbers?',
      info: !userInfo.isBlacklistFree ? <LockIcon /> : (
        <RadioGroup
          row
          aria-labelledby="demo-form-control-label-placement"
          name="position"
          value={requiredBlacklist ? 'Yes' : 'No'}
          onChange={handleBlacklistRadioChange}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio size="small" />}
            label="Yes"
          />
          <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
        </RadioGroup>
      ),
    },
    {
      title: 'Would you like to exclude all DNC phone numbers? (this will remove a large portion of your skipped records)',
      info: !userInfo.isBlacklistFree ? <LockIcon /> : (
        <RadioGroup
          row
          aria-labelledby="demo-form-control-label-placement"
          name="position"
          value={requiredDnc ? 'Yes' : 'No'}
          onChange={handleDncRadioChange}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio size="small" />}
            label="Yes"
            disabled={!requiredBlacklist}
          />
          <FormControlLabel value="No" control={<Radio size="small" />} label="No" disabled={!requiredBlacklist} />
        </RadioGroup>
      ),
    },
    {
      title: 'Total Price:',
      info: (
        <Typography color="primary" sx={{ fontWeight: 'bold', fontSize: '24px' }}>
          ${totalPrice.toFixed(2)}
        </Typography>
      ),
    },
  ];

  const othersSectionArray = [
    {
      title: 'Skip Trace Type:',
      info: <h2>{fileInfo.skipTraceType}</h2>,
    },
    {
      title: 'Total Records:',
      info: <h2>{fileInfo.records - 1}</h2>,
    },
    {
      title: 'Discount:',
      info: <h2>{discount}%</h2>,
    },
    {
      title: 'Price/record:',
      info: <h2>${currentPricePerRecord}</h2>,
    },
    {
      title: 'Total Price:',
      info: (
        <Typography color="primary" sx={{ fontWeight: 'bold', fontSize: '24px' }}>
          ${totalPrice.toFixed(2)}
        </Typography>
      ),
    },
  ];

  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  return (
    <>
      <BillModal
        {...props}
        open={props.open && !isThankYouOpen}
        sectionArray={
          SkipTraceFileTypeENUM.OWNER_MAILING_ADDRESS === fileInfo.skipTraceType
            ? sectionArray
            : othersSectionArray
        }
        isChecked={isChecked}
        handleCheckboxChange={handleCheckboxChange}
        onClickPayBulkSkip={onClickPayBulkSkip}
        isSubmiting={isSubmiting}
      />
      <ThankYouModal
        open={isThankYouOpen}
        estimatedMinutes={estimatedMinutes}
        onGoToDashboard={handleGoToDashboard}
      />
    </>
  );
};

export default Bill;
